import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, FolderKanban, FileText, TrendingUp, GitBranch, Send,
  MessageCircleQuestion, Building2, LogOut, Menu, X, Bell, ExternalLink,
  Radar, LineChart, MapPin, MessageCircle, FolderArchive,
} from "lucide-react";
import { useClientAuth } from "./lib/auth";
import { clientApi } from "./lib/api";
import HectorBot from "./components/HectorBot";
import DemandModal from "./components/DemandModal";
import NPSModal from "./components/NPSModal";

const BASE_NAV = [
  { to: "/cliente", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "cliente-nav-dashboard" },
  { to: "/cliente/projetos", label: "Projetos & Campanhas", icon: FolderKanban, testid: "cliente-nav-projetos" },
  { to: "/cliente/documentos", label: "Documentos", icon: FileText, testid: "cliente-nav-documentos" },
  { to: "/cliente/financeiro", label: "Financeiro", icon: TrendingUp, testid: "cliente-nav-financeiro" },
  { to: "/cliente/acompanhamento", label: "Acompanhamento", icon: GitBranch, testid: "cliente-nav-acompanhamento" },
  { to: "/cliente/demandas", label: "Demandas", icon: Send, testid: "cliente-nav-demandas" },
];

const FEATURE_NAV = [
  { key: "archive", to: "/cliente/arquivo", label: "Arquivo de Entregas", icon: FolderArchive, testid: "cliente-nav-arquivo" },
  { key: "orulo", to: "/cliente/radar-orulo", label: "Radar Órulo", icon: Radar, testid: "cliente-nav-orulo" },
  { key: "fipezap", to: "/cliente/radar-fipezap", label: "Radar FipeZap", icon: LineChart, testid: "cliente-nav-fipezap" },
  { key: "ibge", to: "/cliente/ibge", label: "IBGE Demográfico", icon: MapPin, testid: "cliente-nav-ibge" },
  { key: "talkie", to: "/cliente/talkie", label: "Pesquisas Talkie", icon: MessageCircle, testid: "cliente-nav-talkie" },
];

export default function ClientLayout() {
  const { user, loading, logout } = useClientAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demandOpen, setDemandOpen] = useState(false);
  const [hectorOpen, setHectorOpen] = useState(false);
  const [features, setFeatures] = useState({});
  const [pendingNps, setPendingNps] = useState([]);
  const [npsOpen, setNpsOpen] = useState(false);
  const { pathname } = useLocation();

  const loadNps = () => clientApi.get("/nps").then(({ data }) => setPendingNps((data || []).filter((n) => !n.responded && n.status !== "closed"))).catch(() => {});

  useEffect(() => {
    if (user) {
      clientApi.get("/features").then(({ data }) => setFeatures(data)).catch(() => {});
      loadNps();
    }
  }, [user]);

  const NAV = [
    ...BASE_NAV,
    ...FEATURE_NAV.filter((n) => features[n.key]),
  ];

  const hasPendingNps = pendingNps.length > 0;

  if (loading) return <div className="min-h-screen theme-dark flex items-center justify-center text-neutral-400">Carregando…</div>;
  if (!user) return <Navigate to="/cliente/login" replace />;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* SIDEBAR (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/5 bg-black z-30">
        <Link to="/" className="p-6 border-b border-white/5 block">
          <div className="font-display text-2xl tracking-tighter">places<span className="text-accent-orange">.</span></div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Área do Cliente</div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-4 px-3 no-scrollbar space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-lg transition ${isActive ? "bg-accent-orange text-black font-semibold" : "text-neutral-300 hover:bg-white/5"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
          <button
            onClick={() => setDemandOpen(true)}
            data-testid="cliente-nav-demandar"
            className="mt-4 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-accent-orange to-accent-yellow hover:from-accent-yellow hover:to-accent-orange text-black font-bold px-3 py-3.5 rounded-full text-sm transition-all shadow-2xl shadow-accent-orange/40 hover:scale-[1.02]"
          >
            <Send className="w-4 h-4" /> DEMANDAR
          </button>

          {hasPendingNps && (
            <motion.button
              onClick={() => setNpsOpen(true)}
              data-testid="cliente-nav-nps"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: [1, 0.55, 1] }}
              transition={{ opacity: { duration: 1.4, repeat: Infinity, ease: "easeInOut" } }}
              className="mt-2 w-full relative flex items-center justify-center gap-2 bg-accent-yellow text-black font-bold px-3 py-3 rounded-full text-[11px] transition-all shadow-2xl shadow-accent-yellow/50 border-2 border-accent-yellow"
            >
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-90" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
              <Bell className="w-3.5 h-3.5" /> RESPONDER PESQUISA
              {pendingNps.length > 1 && <span className="ml-1 bg-black text-accent-yellow text-[9px] rounded-full px-1.5 py-0.5">{pendingNps.length}</span>}
            </motion.button>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-2 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-accent-orange text-black flex items-center justify-center text-xs font-bold">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="truncate text-xs font-semibold">{user.company || user.name || user.email}</div>
              <div className="text-[10px] text-neutral-500 truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="cliente-logout" className="mt-2 w-full flex items-center justify-center gap-2 text-xs text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 py-2 rounded-lg transition">
            <LogOut className="w-3 h-3" /> Sair
          </button>
        </div>
      </aside>

      {/* MOBILE HEADER */}
      <header className="lg:hidden sticky top-0 z-20 bg-black/90 backdrop-blur-md border-b border-white/5 px-4 py-3 flex items-center justify-between gap-2">
        <button onClick={() => setMobileOpen(true)} data-testid="cliente-mobile-menu" className="p-2"><Menu className="w-5 h-5" /></button>
        <Link to="/" className="font-display text-xl">places<span className="text-accent-orange">.</span></Link>
        <div className="flex items-center gap-1.5">
          {hasPendingNps && (
            <motion.button onClick={() => setNpsOpen(true)} animate={{ opacity: [1, 0.55, 1] }} transition={{ duration: 1.4, repeat: Infinity }} className="relative bg-accent-yellow text-black font-bold text-[10px] px-2.5 py-1.5 rounded-full">
              <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
              NPS
            </motion.button>
          )}
          <button onClick={() => setDemandOpen(true)} className="bg-accent-orange text-black font-bold text-[11px] px-3 py-1.5 rounded-full">DEMANDAR</button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} className="lg:hidden fixed inset-y-0 left-0 w-72 bg-black z-40 border-r border-white/5 p-4">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-2xl">places<span className="text-accent-orange">.</span></div>
              <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="space-y-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2.5 px-3 py-3 text-sm rounded-lg ${isActive ? "bg-accent-orange text-black font-semibold" : "text-neutral-300"}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </NavLink>
              ))}
            </nav>
            <button onClick={logout} className="mt-6 w-full flex items-center justify-center gap-2 text-xs text-neutral-400 border border-white/10 py-2 rounded-lg">
              <LogOut className="w-3 h-3" /> Sair
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MAIN */}
      <main className="lg:ml-64 p-4 md:p-8 max-w-[1400px]">
        <Outlet />
      </main>

      {/* Floating DEMANDAR button (always visible on all sizes) */}
      <button
        onClick={() => setDemandOpen(true)}
        data-testid="floating-demandar"
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-gradient-to-r from-accent-orange to-accent-yellow hover:from-accent-yellow hover:to-accent-orange text-black font-bold px-5 py-3 rounded-full text-sm transition-all shadow-2xl hover:scale-105"
      >
        <Send className="w-4 h-4" /> DEMANDAR
      </button>

      {/* Floating Hector button */}
      <button
        onClick={() => setHectorOpen((s) => !s)}
        data-testid="hector-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-accent-orange to-accent-yellow shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
        aria-label="Abrir Hector"
      >
        <MessageCircleQuestion className="w-6 h-6 text-black" />
      </button>

      <AnimatePresence>
        {hectorOpen && <HectorBot onClose={() => setHectorOpen(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {demandOpen && <DemandModal onClose={() => setDemandOpen(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {npsOpen && (
          <NPSModal
            surveys={pendingNps}
            onClose={() => setNpsOpen(false)}
            onDone={loadNps}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
